import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import AnimatedWords from './AnimatedWords';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const Services = () => {
  const [activeService, setActiveService] = useState(0);
  const [loadedById, setLoadedById] = useState({});
  const containerRef = useRef(null);
  const mediaRefs = useRef({});
  const prevActiveRef = useRef(0);
  const isInitialMount = useRef(true);

  const services = useMemo(() => [
    {
      id: 0,
      title: "Freight Forwarding",
      description:
        "Comprehensive management of shipments, both domestically and internationally, ensuring smooth delivery from origin to destination.",
      image: "/images/fright_forwarding.webp",
      imageAlt: "Container ship at sea",
    },
    {
      id: 1,
      title: "Warehousing & Distribution",
      description: "Secure storage facilities with efficient inventory management and distribution tailored to your supply chain needs.",
      image: "/images/warehousing_distribution.webp",
      imageAlt: "Warehouse operations",
    },
    {
      id: 2,
      title: "Customs Clearance",
      description: "Expert handling of documentation and compliance to ensure hassle-free cross-border shipping and faster clearance.",
      image: "/images/customs_clearance.webp",
      imageAlt: "Customs clearance process",
    },
    {
      id: 3,
      title: "Last-Mile Delivery",
      description: "Timely and reliable delivery solutions that connect your shipments directly to the end customer with precision.",
      image: "/images/last_mile_delivery.webp",
      imageAlt: "Last mile delivery truck",
    },
    {
      id: 4,
      title: "Express Cargo Services",
      description: "Fast and flexible air and road cargo services designed to meet urgent business demands with reliability.",
      image: "/images/express_cargo_services.webp",
      imageAlt: "Express cargo services",
    },
    {
      id: 5,
      title: "Supply Chain Solutions",
      description: "End-to-end logistics strategies that optimize operations, reduce costs, and drive long-term efficiency.",
      image: "/images/supply_chains_solution.webp",
      imageAlt: "Supply chain management",
    },
  ], []);

  useEffect(() => {
    services.forEach((svc) => {
      const img = new Image();
      img.src = svc.image;
      img.onload = () => {
        setLoadedById((prev) => ({ ...prev, [svc.id]: true }));
      };
      img.onerror = () => {
        setLoadedById((prev) => ({ ...prev, [svc.id]: true }));
      };
    });
  }, [services]);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      // Heading word-by-word reveal on scroll
      const tl = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 75%',
          end: 'bottom 20%',
          toggleActions: 'play none none none',
          once: true
        }
      });
      const headingWords = containerRef.current?.querySelectorAll('.services-head h2 [data-animated-word]');
      if (headingWords && headingWords.length) {
        tl.fromTo(headingWords, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06, clearProps: 'transform' }, 0);
      }

      // Initialize accordion panels (only on initial mount)
      if (isInitialMount.current) {
        services.forEach((svc) => {
          const el = mediaRefs.current[svc.id];
          if (!el) return;
          gsap.set(el, { overflow: 'hidden' });
          if (svc.id === activeService) {
            // Animate the default open accordion smoothly on initial load
            gsap.set(el, { display: 'block', overflow: 'hidden', pointerEvents: 'auto' });
            const autoHeight = el.scrollHeight;
            gsap.fromTo(el, { height: 0, opacity: 0 }, {
              height: autoHeight,
              opacity: 1,
              duration: 0.3,
              ease: 'power2.out',
              onComplete: () => {
                gsap.set(el, { height: 'auto' });
              }
            });
          } else {
            gsap.set(el, { height: 0, opacity: 0, pointerEvents: 'none' });
          }
        });
        prevActiveRef.current = activeService;
        isInitialMount.current = false;
      }
    }, containerRef);

    return () => ctx.revert();
  }, [services, activeService]);

  useLayoutEffect(() => {
    const prev = prevActiveRef.current;
    if (prev === activeService) return;

    const prevEl = prev !== null && prev !== undefined ? mediaRefs.current[prev] : null;
    const curEl = activeService !== null && activeService !== undefined ? mediaRefs.current[activeService] : null;

    // If there's a previous element to close and a new one to open, sequence them
    if (prevEl && curEl) {
      // Kill any existing animations
      gsap.killTweensOf(prevEl);
      gsap.killTweensOf(curEl);

      // Ensure previous element is visible and get its actual height
      // If it has height: 'auto', we need to measure it properly
      gsap.set(prevEl, { height: 'auto', overflow: 'hidden' });
      // Force a reflow to ensure scrollHeight is accurate
      void prevEl.offsetHeight;
      const currentHeight = prevEl.scrollHeight;
      
      // Now set it to the measured height for animation
      gsap.set(prevEl, { height: currentHeight, overflow: 'hidden' });

      // Prepare new element for opening
      gsap.set(curEl, { display: 'block', overflow: 'hidden', height: 0, opacity: 0, pointerEvents: 'auto' });
      const autoHeight = curEl.scrollHeight;

      // Create timeline to sequence animations smoothly
      const tl = gsap.timeline();
      
      // Close previous accordion
      tl.to(prevEl, {
        height: 0,
        opacity: 0,
        duration: 0.35,
        ease: 'power2.in',
        onComplete: () => {
          gsap.set(prevEl, { pointerEvents: 'none' });
        }
      });

      // Open new accordion - start after previous closes
      tl.to(curEl, {
        height: autoHeight,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          gsap.set(curEl, { height: 'auto' });
        }
      }, '+=0.05'); // Small delay after closing completes for smoother transition

    } else if (prevEl) {
      // Only closing (when clicking an open accordion to close it)
      gsap.killTweensOf(prevEl);
      // Ensure we get the actual height even if it's set to 'auto'
      gsap.set(prevEl, { height: 'auto', overflow: 'hidden' });
      // Force a reflow to ensure scrollHeight is accurate
      void prevEl.offsetHeight;
      const currentHeight = prevEl.scrollHeight;
      gsap.set(prevEl, { overflow: 'hidden', height: currentHeight });
      gsap.to(prevEl, {
        height: 0,
        opacity: 0,
        duration: 0.35,
        ease: 'power2.in',
        onComplete: () => {
          gsap.set(prevEl, { pointerEvents: 'none' });
        }
      });
    } else if (curEl) {
      // Only opening (initial load or first selection)
      gsap.killTweensOf(curEl);
      gsap.set(curEl, { display: 'block', overflow: 'hidden', pointerEvents: 'auto' });
      const autoHeight = curEl.scrollHeight;
      gsap.fromTo(curEl, { height: 0, opacity: 0 }, {
        height: autoHeight,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          gsap.set(curEl, { height: 'auto' });
        }
      });
    }

    prevActiveRef.current = activeService;
  }, [activeService]);

  const handleServiceClick = (serviceId) => {
    // If clicking the same accordion, close it
    // If clicking a different accordion, close current and open new one
    if (activeService === serviceId) {
      setActiveService(null);
    } else {
      setActiveService(serviceId);
    }
  };

  return (
    <section id="services" className="section services bg-[#FFF6F6]" ref={containerRef}>
      <div className="services-head text-center mb-8">
        <div className="container section-badge flex justify-center items-center gap-3 text-base md:text-lg lg:text-xl">
          <div className="badge-dot"></div>
          Our Services
        </div>

        <h2 className="text-2xl md:text-3xl lg:text-5xl">
          <AnimatedWords auto={false} lines={["Innovative Logistic Services"]} />
        </h2>
      </div>

      <div className="container services-list" role="list">
        {services.map((service, index) => (
          <article
            key={service.id}
            className={`services-item border-b border-gray-200   sm:pb-0 cursor-pointer ${activeService === service.id ? "active" : ""}`}
            role="listitem"
            onClick={() => handleServiceClick(service.id)}
          >
            {/* Top row */}
            <div className="services-row flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* number + title group */}
              <div className="flex items-center justify-center sm:justify-start gap-4 sm:gap-6">
                <span className="num text-gray-600 text-lg sm:text-lg">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="title text-[20px] sm:text-[24px] font-semibold sm:hidden">
                  {service.title}
                </div>
              </div>

              <div className="hidden title text-[20px] sm:text-[24px] font-semibold sm:flex">
                {service.title}
              </div>

              {/* description only visible on desktop */}
              {service.description && activeService === service.id && (
                <p className="desc hidden sm:block text-[17px] text-gray-700 text-center sm:text-left flex-1 sm:ml-6 sm:max-w-[34%]">
                  {service.description}
                </p>
              )}

              {/* arrow button (hidden on mobile) */}
              <button
                className="pill hidden sm:flex justify-center items-center"
                aria-label="View service"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.5885 13.9584H0.833252V11.0417H18.5885L10.4218 2.87504L12.4999 0.833374L24.1666 12.5L12.4999 24.1667L10.4218 22.125L18.5885 13.9584Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>

            {/* Image (always visible when active) */}
            <div
              ref={(el) => { mediaRefs.current[service.id] = el; }}
              className={`services-media`}
              aria-hidden={activeService !== service.id}
            >
              <img
                src={service.image}
                alt={service.imageAlt}
                decoding="async"
                fetchPriority={activeService === service.id ? "high" : "low"}
                className={`w-full h-auto transition-opacity duration-500 ${loadedById[service.id] ? "is-loaded" : "opacity-0"}`}
              />

              {/* Mobile-only description under image */}
              {service.description && activeService === service.id && (
                <p className="desc block sm:hidden text-[16px] text-gray-700 lg:w-[40%]lg:max-w-[40%] text-justify mt-4 px-4">
                  {service.description}
                </p>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default Services;
